import React, { useState, useEffect } from 'react';
import TopNav from './TopNav';
import { useAuth } from './common/Auth';
import { useCurrentSystem } from './context/SystemContext';

import Home from './Home';
import Admin from './Admin';
import Agent from './Agent';
import Account from './Account';
import ScenesView from './views/ScenesView';
import ScheduleView from './views/ScheduleView';
import UsersView from './views/UsersView';
import GatewaysView from './views/GatewaysView';
import UserAvatar from './components/UserAvatar';

import icon from './simulcasticon.svg';
import {
	Palette,
	Calendar,
	Users,
	Router,
	LayoutGrid,
	Settings,
	Briefcase,
	BookOpen,
	MoreHorizontal,
	X,
} from 'lucide-react';

const SYSTEM_VIEWS = ['scenes', 'schedule', 'users', 'gateways'];

const Main = ({ history }) => {
	const [view, setView] = useState('systems');
	const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
	const currentUser = useAuth();
	const { currentSystem, setCurrentSystemUID } = useCurrentSystem();

	const roles = (currentUser && currentUser.roles) || [];
	const canSeeAdmin = roles.includes('admin');
	const canSeeAgent = roles.includes('agent');

	const changeView = (v) => {
		if (v === 'admin' && !canSeeAdmin) v = 'systems';
		if (v === 'agent' && !canSeeAgent) v = 'systems';
		if (SYSTEM_VIEWS.includes(v) && !currentSystem) return;
		if (v === 'systems') {
			setCurrentSystemUID('');
		}
		setView(v);
		setMobileMoreOpen(false);
	};

	const selectSystem = (uid) => {
		setCurrentSystemUID(uid);
		setView('scenes');
		setMobileMoreOpen(false);
	};

	const showSystemNav = SYSTEM_VIEWS.includes(view);

	const topNavItems = [
		{ key: 'scenes', label: 'Scenes', icon: Palette },
		{ key: 'schedule', label: 'Schedule', icon: Calendar },
		{ key: 'users', label: 'Users', icon: Users },
		{ key: 'gateways', label: 'Setup', icon: Router },
	];

	const bottomNavItems = [
		{ key: 'systems', label: 'Systems', icon: LayoutGrid },
	];

	const moreNavItems = [
		...(canSeeAdmin ? [{ key: 'admin', label: 'Admin', icon: Settings }] : []),
		...(canSeeAgent ? [{ key: 'agent', label: 'Agent', icon: Briefcase }] : []),
		...bottomNavItems,
		{ key: 'account', label: 'Account', icon: null, avatar: true },
	];

	useEffect(() => {
		if (view === 'admin' && !canSeeAdmin) setView('systems');
		if (view === 'agent' && !canSeeAgent) setView('systems');
	}, [view, canSeeAdmin, canSeeAgent]);

	const isMoreViewActive = moreNavItems.some((item) => item.key === view);

	const renderDocsLink = (className = '') => (
		<a
			href="/docs"
			target="_blank"
			rel="noopener noreferrer"
			className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl transition-all font-medium text-sm hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 ${className}`}
		>
			<BookOpen size={17} />
			<span className="truncate">Documentation</span>
		</a>
	);

	const renderNavButton = ({ key, label, icon: Icon, avatar }) => (
		<button
			key={key}
			onClick={() => changeView(key)}
			className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl transition-all font-medium text-sm ${
				view === key
					? 'bg-[#880e4f] text-white shadow-sm'
					: 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
			}`}
		>
			{avatar ? (
				<UserAvatar user={currentUser} size={28} />
			) : (
				<Icon size={17} />
			)}
			<span className="truncate">{label}</span>
		</button>
	);

	const renderContent = () => {
		if (view === 'account') return <Account />;
		if (view === 'admin' && canSeeAdmin) return <Admin />;
		if (view === 'agent' && canSeeAgent) return <Agent />;
		if (view === 'systems') return <Home onSystemSelected={selectSystem} />;
		if (view === 'scenes') return <ScenesView />;
		if (view === 'schedule') return <ScheduleView />;
		if (view === 'users') return <UsersView />;
		if (view === 'gateways') return <GatewaysView />;
		return <Home />;
	};

	return (
		<div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-950">
			<TopNav
				history={history}
				icon={icon}
				homeClicked={() => { setCurrentSystemUID(''); changeView('systems'); }}
			/>

			<div className="flex flex-1 overflow-hidden">
				<aside className="hidden md:flex w-56 border-r bg-white dark:bg-gray-900 flex-col shrink-0">
					<div className="p-4 border-b dark:border-gray-800">
						<div className="text-sm font-semibold">Current System</div>
						<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" title={currentSystem?.name || currentSystem?.uid || ''}>
							{currentSystem ? (currentSystem.name || currentSystem.uid) : 'None selected'}
						</div>
					</div>

					<nav className="flex-1 flex flex-col min-h-0">
						{showSystemNav ? (
							<div className="flex-1 p-3 space-y-1 text-sm overflow-y-auto">
								{topNavItems.map((item) => renderNavButton(item))}
							</div>
						) : (
							<div className="flex-1 p-3 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
								Select a system below to manage scenes, schedules, users, and setup.
							</div>
						)}

						<div className="p-3 border-t dark:border-gray-800 space-y-1 text-sm">
							{canSeeAdmin && renderNavButton({ key: 'admin', label: 'Admin', icon: Settings })}
							{canSeeAgent && renderNavButton({ key: 'agent', label: 'Agent', icon: Briefcase })}
							{bottomNavItems.map((item) => renderNavButton(item))}
							{renderDocsLink()}
							<button
								onClick={() => changeView('account')}
								className={`w-full flex items-center gap-3 px-3.5 py-2 rounded-xl transition-all font-medium ${
									view === 'account'
										? 'bg-[#880e4f] text-white shadow-sm'
										: 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
								}`}
							>
								<UserAvatar user={currentUser} size={28} />
								<span className="truncate">Account</span>
							</button>
						</div>
					</nav>
				</aside>

				<main className="flex-1 overflow-auto pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0">
					{renderContent()}
				</main>
			</div>

			{/* Mobile bottom navigation */}
			<nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 pb-[env(safe-area-inset-bottom)]">
				<div className="flex items-stretch">
					{showSystemNav ? (
						topNavItems.map((item) => {
							const Icon = item.icon;
							const active = view === item.key;
							return (
								<button
									key={item.key}
									onClick={() => changeView(item.key)}
									className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[3.25rem] transition-colors ${
										active
											? 'text-[#880e4f]'
											: 'text-gray-500 dark:text-gray-400'
									}`}
								>
									<Icon size={20} strokeWidth={active ? 2.5 : 2} />
									<span className="text-[10px] font-medium leading-none">{item.label}</span>
								</button>
							);
						})
					) : (
						<button
							onClick={() => changeView('systems')}
							className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[3.25rem] transition-colors ${
								view === 'systems'
									? 'text-[#880e4f]'
									: 'text-gray-500 dark:text-gray-400'
							}`}
						>
							<LayoutGrid size={20} strokeWidth={view === 'systems' ? 2.5 : 2} />
							<span className="text-[10px] font-medium leading-none">Systems</span>
						</button>
					)}
					<button
						onClick={() => setMobileMoreOpen((open) => !open)}
						className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[3.25rem] transition-colors ${
							isMoreViewActive || mobileMoreOpen
								? 'text-[#880e4f]'
								: 'text-gray-500 dark:text-gray-400'
						}`}
					>
						<MoreHorizontal size={20} />
						<span className="text-[10px] font-medium leading-none">More</span>
					</button>
				</div>
			</nav>

			{mobileMoreOpen && (
				<>
					<div
						className="md:hidden fixed inset-0 z-50 bg-black/40"
						onClick={() => setMobileMoreOpen(false)}
					/>
					<div className="md:hidden fixed bottom-[calc(3.25rem+env(safe-area-inset-bottom))] inset-x-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 rounded-t-2xl shadow-xl p-3 pb-4 max-h-[60vh] overflow-y-auto">
						<div className="flex items-center justify-between mb-2 px-1">
							<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">More</span>
							<button
								onClick={() => setMobileMoreOpen(false)}
								className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
							>
								<X size={18} />
							</button>
						</div>
						{currentSystem && SYSTEM_VIEWS.includes(view) && (
							<div className="text-xs text-gray-500 dark:text-gray-400 px-1 mb-2 truncate">
								{currentSystem.name || currentSystem.uid}
							</div>
						)}
						<div className="space-y-1">
							{moreNavItems.map((item) => renderNavButton(item))}
							{renderDocsLink()}
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default Main;
